import {
  ApolloClient,
  ApolloProvider,
  from,
  gql,
  InMemoryCache,
  Observable,
} from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";

import { createUploadLink } from "apollo-upload-client";
import { Modal } from "components";
import { env } from "configs";
import { t } from "i18next";
import appStore from "stores/appStore";
import PropTypes from "prop-types";
const TOKEN = "AUTHENTICATION_TOKEN";
export const getToken = () => localStorage.getItem(TOKEN);
export const setToken = (token) => localStorage.setItem(TOKEN, token);
export const removeToken = () => localStorage.removeItem(TOKEN);

const REFRESH_TOKEN = "REFRESH_TOKEN";
export const getRefreshToken = () => localStorage.getItem(REFRESH_TOKEN);
export const setRefreshToken = (token) =>
  localStorage.setItem(REFRESH_TOKEN, token);
export const removeRefreshToken = () => localStorage.removeItem(REFRESH_TOKEN);

// API ตอบ code นี้เมื่อ JWT ยัง verify ผ่าน แต่ session ถูกแทนที่ด้วยการ login
// ครั้งใหม่จากอุปกรณ์อื่น (single session) ต่างจาก 401 ปกติตรงที่ refresh token
// ก็ถูกยกเลิกไปด้วย การพยายาม refresh จึงมีแต่จะวนเปล่า
const SESSION_REVOKED = "SESSION_REVOKED";

// exception ฝั่ง server ส่ง extensions ซ้อนอีกชั้น code จึงอยู่ที่
// extensions.extensions.code รองรับทั้งสองรูปแบบไว้เผื่อ
const getErrorCode = (graphQLError) =>
  graphQLError?.extensions?.extensions?.code ?? graphQLError?.extensions?.code;

const isSessionRevoked = (graphQLErrors) =>
  (graphQLErrors ?? []).some(
    (graphQLError) => getErrorCode(graphQLError) === SESSION_REVOKED
  );

// แยกให้ออกระหว่าง "ต่อ server ไม่ติด" กับ "server บอกว่า token ใช้ไม่ได้"
// เคสแรกห้ามลบ token ทิ้ง ไม่งั้น server restart ทีเดียวผู้ใช้หลุดยกแผงทั้งที่
// session ยังใช้ได้อยู่
const AUTH_ERROR_STATUSES = [401, 403];

const getHttpStatus = (graphQLError) =>
  graphQLError?.extensions?.extensions?.http?.status ??
  graphQLError?.extensions?.exception?.status;

// 401 อาจมาเป็น graphQLErrors (HTTP 200 + errors) หรือมาเป็น networkError ที่มี
// errors อยู่ใน result แล้วแต่ว่า server ตอบ status อะไร ต้องดูทั้งสองทาง
const getAllGraphQLErrors = (error) => [
  ...(error?.graphQLErrors ?? []),
  ...(error?.networkError?.result?.errors ?? []),
];

export const isAuthError = (error) =>
  getAllGraphQLErrors(error).some((graphQLError) =>
    AUTH_ERROR_STATUSES.includes(getHttpStatus(graphQLError))
  ) || AUTH_ERROR_STATUSES.includes(error?.networkError?.statusCode);

// ต่อไม่ติดจริง ๆ : มี networkError แต่ไม่มี graphQL error ให้อ่านเลย
// (connection refused, 502 html, timeout/abort)
export const isConnectionError = (error) =>
  !!error?.networkError && getAllGraphQLErrors(error).length === 0;

let revokedSession = false;

// MSAL ยัง cache accounts ไว้ใน memory ของ React context ด้วย การล้าง storage
// เฉย ๆ จึงยังไม่หยุด effect ที่ auto sign-in จนกว่าจะ reload
export const hasRevokedSession = () => revokedSession;

const isRefreshRequest = (operation) => {
  return operation.operationName === "GEN_REFRESH_TOKEN";
};

// Returns accesstoken if opoeration is not a refresh token request
const returnTokenDependingOnOperation = (operation) => {
  if (isRefreshRequest(operation)) {
    return getRefreshToken() || "";
  } else {
    return getToken() || "";
  }
};

// fetch ไม่มี timeout ในตัว ถ้า server รับ connection แล้วเงียบ (เช่นกำลัง restart)
// promise จะไม่ทั้ง resolve และ reject -> query ค้าง loading:true ตลอดกาล
const REQUEST_TIMEOUT = 60000;
// upload / export ไฟล์ใหญ่กินเวลาได้มากกว่าปกติ แยกเวลาให้ ไม่งั้นจะตัดงานที่ยังดีอยู่
const UPLOAD_TIMEOUT = 300000;

const fetchWithTimeout = (uri, options = {}) => {
  if (typeof AbortController === "undefined") {
    return fetch(uri, options);
  }

  const isUpload =
    typeof FormData !== "undefined" && options.body instanceof FormData;
  const controller = new AbortController();
  const timer = setTimeout(
    () => controller.abort(),
    isUpload ? UPLOAD_TIMEOUT : REQUEST_TIMEOUT
  );

  // createUploadLink ตั้ง signal ของตัวเองไว้ยกเลิก request ตอน unsubscribe
  // ต้องต่อ abort นั้นมาที่ controller ตัวนี้ ไม่งั้นการยกเลิกจะไปไม่ถึง fetch
  if (options.signal) {
    if (options.signal.aborted) {
      controller.abort();
    } else {
      options.signal.addEventListener("abort", () => controller.abort());
    }
  }

  return fetch(uri, { ...options, signal: controller.signal }).finally(() =>
    clearTimeout(timer)
  );
};

const httpLink = createUploadLink({
  uri: env.API_ENDPOINT,
  fetch: fetchWithTimeout,
});

const authLink = setContext((operation, { headers }) => {
  const token = returnTokenDependingOnOperation(operation);
  return {
    headers: {
      ...headers,
      "x-tenant-id": "bam",
      authorization: token ? `${token}` : "",
    },
  };
});

// ห้ามเป็น async: Apollo ต้องการ Observable กลับไป subscribe ถ้าคืน Promise
// มันจะเรียก .subscribe ไม่ได้
const handle401Error = (operation, forward) => {
  if (operation.operationName === "GEN_REFRESH_TOKEN") {
    Modal.alert({
      className: "TokenExpire",
      title: t("client.api.modal401Title"),
      children: t("client.api.modal401Children"),
      okButtonLabel: t("client.api.modal401OkButtonLabel"),
      okButtonVariant: "contained",
      onOk: async ({ close }) => {
        appStore.logout();
      },
      disableBackdropClick: true,
    });
  } else {
    // Retry the failed request
    const observable = new Observable((observer) => {
      (async () => {
        try {
          await refreshToken();

          // Retry the failed request
          const subscriber = {
            next: observer.next.bind(observer),
            error: observer.error.bind(observer),
            complete: observer.complete.bind(observer),
          };

          forward(operation).subscribe(subscriber);
        } catch (err) {
          observer.error(err);
        }
      })();
    });
    return observable;
  }
};

const handleSessionRevokedError = () => {
  revokedSession = true;
  // ทิ้ง token ทันที ไม่ต้องรอผู้ใช้กดปุ่ม request ที่ค้างอยู่จะได้ไม่ยิงต่อด้วย
  // token ที่ตายแล้ว
  removeToken();
  removeRefreshToken();
  Modal.alert({
    className: "SessionRevoked",
    title: t("client.api.modalSessionRevokedTitle"),
    children: t("client.api.modalSessionRevokedChildren"),
    okButtonLabel: t("client.api.modalSessionRevokedOkButtonLabel"),
    okButtonVariant: "contained",
    onOk: async ({ close }) => {
      // appStore.logout() ล้าง sessionStorage ด้วย ซึ่งเป็นที่ที่ MSAL เก็บ cache ไว้
      // (ดู constants/msalConfig) ถ้าเหลือ account ไว้ Routes จะ signInWithAzureAd
      // ใหม่อัตโนมัติ แล้วเครื่องนี้จะแย่ง session กลับจากเครื่องที่เพิ่ง login
      appStore.logout();
      close();
    },
    disableBackdropClick: true,
  });
};

const handle403Error = () => {
  Modal.alert({
    title: t("client.api.modal403Title"),
    children: t("client.api.modal403Children"),
    okButtonLabel: t("client.api.modal403OkButtonLabel"),
    okButtonVariant: "contained",
    onOk: async ({ close }) => {
      appStore.logout();
      close();
    },
    disableBackdropClick: true,
  });
};

const handle500Error = (graphQLErrors) => {
  Modal.alert({
    title: t("client.api.modal500Title"),
    children: graphQLErrors[0]?.message
      ? t(graphQLErrors[0]?.message)
      : "เกิดข้อผิดพลาดบางอย่าง โปรดติดต่อทีมงาน",
    okButtonLabel: t("client.api.modal500OkButtonLabel"),
    okButtonVariant: "contained",
    onOk: async ({ close }) => {
      close();
    },
    disableBackdropClick: true,
  });
};

const handle404Error = () => {
  window.history.back();
};

const errorLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    // ถ้า handler นี้โยน exception ออกไป Apollo จะไม่ได้เรียก observer.error/next
    // ต่อ = query ค้าง loading:true ตลอดกาล และ splash screen ไม่มีวันหาย
    // (ดู @apollo/client/link/error - errorHandler ถูกเรียกนอก try/catch)
    try {
      // เช็คก่อน 401 เพราะ handle401Error จะไปเรียก refresh token ซึ่งถูกยกเลิก
      // ไปพร้อมกับ session แล้ว วนไปก็ไม่สำเร็จ
      if (isSessionRevoked(graphQLErrors)) {
        handleSessionRevokedError();
        return;
      }

      if (graphQLErrors?.[0]?.extensions?.exception?.status === 404) {
        handle404Error();
        return;
      }

      if (graphQLErrors) {
        switch (graphQLErrors[0]?.extensions?.extensions?.http?.status) {
          case 401:
            // ต้อง return observable ที่ handle401Error สร้าง ไม่งั้นไม่มีใคร
            // subscribe แล้ว executor ข้างในจะไม่ทำงาน = ไม่เคย refresh token เลย
            return handle401Error(operation, forward);
          case 403:
            handle403Error();
            return;
          case 500:
            handle500Error(graphQLErrors);
            return;
          default:
            return;
        }
      }

      if (networkError) console.log(`[Network error]: ${networkError}`);
    } catch (e) {
      console.error("[errorLink] handler crashed:", e);
    }
  }
);

const GEN_REFRESH_TOKEN = gql`
  mutation GEN_REFRESH_TOKEN {
    refreshBackofficeUserRefreshToken {
      accessToken
      refreshToken
    }
  }
`;
const client = new ApolloClient({
  link: from([errorLink, authLink, httpLink]),
  cache: new InMemoryCache({
    addTypename: false,
  }),
  connectToDevTools: true,
});

// handle401Error รอ refresh ด้วย await ถ้า mutation นี้ไม่ settle (server ไม่ตอบ
// แต่ไม่ปิด socket) query ที่รอ retry อยู่ก็ไม่ settle ตาม -> หน้า loading ค้างถาวร
const REFRESH_TOKEN_TIMEOUT = 15000;

const withTimeout = (promise, ms, message) => {
  let timer;
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(message)), ms);
    }),
  ]).finally(() => clearTimeout(timer));
};

const requestRefreshToken = async () => {
  try {
    const refreshResolverResponse = await client.mutate({
      mutation: GEN_REFRESH_TOKEN,
    });

    const refreshData =
      refreshResolverResponse.data?.refreshBackofficeUserRefreshToken;
    if (!refreshData) {
      throw new Error("Refresh token data is missing");
    }

    const { accessToken, refreshToken } = refreshData;
    setToken(accessToken);
    setRefreshToken(refreshToken);
    return accessToken;
  } catch (err) {
    localStorage.clear();
    throw err;
  }
};

let refreshTokenPromise = null;

// หลาย query โดน 401 พร้อมกันได้ ถ้าปล่อยให้ยิง refresh คนละครั้ง ตัวที่ยิงทีหลัง
// จะใช้ refresh token ที่ถูก rotate ไปแล้วและพังยกชุด จึงต้องแชร์ request เดียวกัน
const refreshToken = () => {
  if (!refreshTokenPromise) {
    refreshTokenPromise = withTimeout(
      requestRefreshToken(),
      REFRESH_TOKEN_TIMEOUT,
      "Refresh token timed out"
    ).finally(() => {
      refreshTokenPromise = null;
    });
  }
  return refreshTokenPromise;
};

export const ApiProvider = (props) => (
  <ApolloProvider client={client}>{props.children}</ApolloProvider>
);
ApiProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const resetStore = client.resetStore;
export const clearStore = client.clearStore;

export { gql };

export default client;
